import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { nominationAPI } from '../../lib/atoNominationAPI';
import { errText, auDate } from '../../lib/complianceAutoAPI';

const ACTIVE = ['nomination_requested', 'help_requested', 'not_found', 're_nomination_required', 'client_confirmed', 'verification_pending', 'nomination_verified', 'agent_linking_pending', 'linking_failed'];

export const AuthorisedPersonPanel = ({ r, reload }) => {
  const [open, setOpen] = useState(false);
  const [people, setPeople] = useState([]);
  const [pick, setPick] = useState('');
  const [n, setN] = useState({ name: '', email: '', role: 'director' });
  useEffect(() => { if (open) nominationAPI.contacts(r.id).then(setPeople).catch(() => {}); }, [open, r.id]);
  if (!ACTIVE.includes(r.status)) return null;
  const ap = r.authorised_person;
  const send = async () => {
    try {
      const res = await nominationAPI.sendAuthorisedPerson(r.id, pick && pick !== 'new' ? { person_id: pick } : n);
      toast.success(res.mode === 'invited' ? `Portal invitation sent to ${res.name}` : `${res.name} already has a login – notified`);
      setOpen(false); reload();
    } catch (e) { toast.error(errText(e)); }
  };
  return (
    <div className="text-xs space-y-2" data-testid={`nom-ap-${r.id}`}>
      <div className="flex flex-wrap items-center gap-2">
        {ap ? <span data-testid={`nom-ap-current-${r.id}`}>Being completed by: <b>{ap.name}</b> ({ap.role}) · {ap.mode === 'invited' ? 'invited' : 'existing login notified'} {auDate(ap.sent_at)}{ap.email_status === 'failed' ? ' · email failed – retry' : ''}</span>
          : <span className="text-muted-foreground">Main portal user completes this nomination</span>}
        <Button size="sm" variant="outline" onClick={() => setOpen(!open)} data-testid={`nom-ap-toggle-${r.id}`}>Send instructions to authorised person</Button>
      </div>
      {open && <div className="border rounded p-2 space-y-2 bg-muted/30">
        <select className="border rounded h-8 px-1 w-full" value={pick} onChange={(e) => setPick(e.target.value)} data-testid={`nom-ap-pick-${r.id}`}>
          <option value="">Choose a contact for this entity…</option>{people.filter((p) => p.email).map((p) => <option key={p.id} value={p.id}>{p.name} · {p.role} · {p.email}</option>)}
          <option value="new">+ Add a new contact</option></select>
        {pick === 'new' && <div className="flex flex-wrap gap-2">
          <Input className="h-8 w-44" placeholder="Full name" value={n.name} onChange={(e) => setN({ ...n, name: e.target.value })} data-testid={`nom-ap-name-${r.id}`} />
          <Input className="h-8 w-56" placeholder="Email" value={n.email} onChange={(e) => setN({ ...n, email: e.target.value })} data-testid={`nom-ap-email-${r.id}`} />
          <select className="border rounded h-8 px-1" value={n.role} onChange={(e) => setN({ ...n, role: e.target.value })} data-testid={`nom-ap-role-${r.id}`}>
            {['director', 'trustee', 'public officer', 'partner', 'secretary'].map((x) => <option key={x}>{x}</option>)}</select></div>}
        <p className="text-muted-foreground">They get a secure portal login scoped to this entity only (no group-wide access). Existing logins are notified instead.</p>
        <Button size="sm" disabled={!pick} onClick={send} data-testid={`nom-ap-send-${r.id}`}>Send</Button>
      </div>}
    </div>
  );
};

export const HelpPanel = ({ r, reload }) => {
  const [msg, setMsg] = useState('');
  if (r.status !== 'help_requested' && !r.help_conversation_id) return null;
  const reply = async () => { try { await nominationAPI.helpReply(r, msg); setMsg(''); toast.success('Reply sent to the client portal'); reload(); } catch (e) { toast.error(errText(e)); } };
  const resolve = async () => { try { await nominationAPI.act(r.id, 'help-resolve', { note: 'Help provided' }); toast.success('Help resolved – reminders resumed'); reload(); } catch (e) { toast.error(errText(e)); } };
  return (
    <div className="border rounded p-2 space-y-2 bg-orange-50/60 text-xs" data-testid={`nom-help-${r.id}`}>
      <div><b>Help topic:</b> {r.help_topic_label || '—'} · <i>{r.help_message}</i>
        {r.help_conversation_id && <a className="underline ml-2" href={`/communications?conversation=${r.help_conversation_id}`} data-testid={`nom-help-thread-${r.id}`}>Open thread</a>}</div>
      {r.status === 'help_requested' && <>
        <textarea className="w-full border rounded p-2" rows={2} value={msg} onChange={(e) => setMsg(e.target.value)} placeholder="Reply to the client (sent via portal messages)" data-testid={`nom-help-reply-text-${r.id}`} />
        <div className="flex gap-2"><Button size="sm" disabled={msg.trim().length < 2} onClick={reply} data-testid={`nom-help-reply-${r.id}`}>Send reply</Button>
          <Button size="sm" variant="outline" onClick={resolve} data-testid={`nom-help-resolve-${r.id}`}>Resolve help (resume reminders)</Button></div></>}
    </div>
  );
};

export const ReminderLog = ({ r }) => {
  const [rows, setRows] = useState(null);
  return (
    <details className="text-xs" onToggle={(e) => e.target.open && !rows && nominationAPI.reminders(r.id).then(setRows).catch(() => setRows([]))} data-testid={`nom-reminders-${r.id}`}>
      <summary className="cursor-pointer">Client reminders{r.last_reminder_step ? ` (last: ${r.last_reminder_step})` : ''}</summary>
      {(rows || []).map((x) => <div key={x.id}>{(x.created_at || '').slice(0, 16).replace('T', ' ')} · {x.reminder_type.replace('ato_nomination_', '')} · {x.channel} · {x.recipient_email || ''} · <b>{x.status}</b>{x.error ? ` – ${x.error}` : ''}</div>)}
      {rows && !rows.length && <div className="text-muted-foreground">None yet</div>}
    </details>
  );
};

export const ReportEvidence = ({ r }) => ((r.report_evidence || []).length ? (
  <div className="text-xs text-sky-900 bg-sky-50 rounded p-2" data-testid={`nom-report-evidence-${r.id}`}>
    {r.report_evidence.map((x) => <div key={x.import_ref}>{x.text} · nominated {auDate(x.nomination_date)} · expiry {auDate(x.expiry)} {x.ato_status ? `· ATO status ${x.ato_status}` : ''}</div>)}
    <div className="text-muted-foreground">Evidence only – a staff member must still click Verify.</div></div>) : null);
